// Interpreta o texto lido (OCR) de um documento de identificação — CNH, CNH digital, RG, CIN —
// e extrai os dados do cadastro de cliente: nome, CPF e RG. Funções puras, sem dependências.

export function cpfValido(valor) {
  const c = (valor || '').replace(/\D/g, '');
  if (c.length !== 11 || /^(\d)\1+$/.test(c)) return false;
  const digito = (n) => {
    let soma = 0;
    for (let i = 0; i < n; i++) soma += Number(c[i]) * (n + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return digito(9) === Number(c[9]) && digito(10) === Number(c[10]);
}

// Um número de registro da CNH também tem 11 dígitos, então o CPF formatado (com pontos) tem prioridade
function extrairCpf(texto) {
  const candidatos = [
    /\b\d{3}\.\d{3}\.\d{3}[-.\s]?\d{2}\b/g,
    /\b\d{3}[.\s]?\d{3}[.\s]?\d{3}[-.\s]?\d{2}\b/g,
  ];
  for (const regex of candidatos) {
    const achados = texto.match(regex) || [];
    const valido = achados.find(cpfValido);
    if (valido) return valido.replace(/\D/g, '');
  }
  return '';
}

const PALAVRAS_QUE_NAO_SAO_NOME = /DATA|LOCAL|FILIA|REGISTRO|HABILITA|NASCIMENTO|VALIDADE|NACIONALIDADE|CATEGORIA|OBSERVA|ASSINATURA|IDENTIDADE|EMISS|BRASIL|REP[UÚ]BLICA|MINIST|SECRETARIA/;

// Deixa só letras e espaços; devolve '' se o resultado não parecer um nome de pessoa
function limparNome(bruto) {
  const texto = bruto
    .replace(/\d{1,2}\/\d{1,2}\/\d{2,4}/g, ' ') // datas coladas ao nome
    .replace(/[^A-ZÀ-Ý' ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  // descarta letras soltas do lixo do OCR nas pontas (ex.: "E MARCOS DANIEL ..." vindo da moldura)
  const palavras = texto.split(' ').filter(Boolean);
  while (palavras.length && palavras[0].length < 2) palavras.shift();
  while (palavras.length && palavras[palavras.length - 1].length < 2) palavras.pop();
  const nome = palavras.join(' ');
  // Todo nome de verdade tem vogais em cada palavra; isso descarta lixo como "DD MM YYYY"
  const soConsoantes = palavras.some(p => !/[AEIOUÀ-Ý]/.test(p));
  if (palavras.length < 2 || nome.length < 5 || soConsoantes || PALAVRAS_QUE_NAO_SAO_NOME.test(nome)) return '';
  return nome;
}

const ROTULO_NOME = /\bNOME(\s+E\s+SOBRENOME|\s+COMPLETO)?\b/i;
const NAO_E_ROTULO_DE_NOME = /M[ÃA]E|\bPAI\b|FILIA|SOCIAL/i;

function extrairNome(linhas) {
  for (let i = 0; i < linhas.length; i++) {
    if (!ROTULO_NOME.test(linhas[i]) || NAO_E_ROTULO_DE_NOME.test(linhas[i])) continue;
    // O nome pode vir na mesma linha do rótulo (depois dele) ou na linha seguinte
    const resto = linhas[i].replace(/^.*?\bNOME(\s+E\s+SOBRENOME|\s+COMPLETO)?\b[\s/:.-]*/i, '');
    const candidatos = [resto, linhas[i + 1] || '', linhas[i + 2] || ''];
    for (const candidato of candidatos) {
      const nome = limparNome(candidato);
      if (nome) return nome;
    }
  }
  // Reserva: linha de leitura óptica no verso da CNH, ex.: MARCOS<<DANIEL<BEHLING<RADATZ<<<<
  for (const linha of linhas) {
    if (/^[A-Z]{2,}(<+[A-Z]+)+<*$/.test(linha.replace(/\s/g, ''))) {
      const nome = limparNome(linha.replace(/</g, ' '));
      if (nome) return nome;
    }
  }
  return '';
}

const ROTULO_RG = /IDENTIDADE|REGISTRO\s+GERAL|\bRG\b/i;
const NUMERO_RG = /\b\d[\d.-]{4,}[\dXx]\b/;

function extrairRg(linhas, cpf) {
  const cpfDigitos = cpf || '';
  for (let i = 0; i < linhas.length; i++) {
    if (!ROTULO_RG.test(linhas[i])) continue;
    const resto = linhas[i].replace(/^.*?(IDENTIDADE|REGISTRO\s+GERAL|\bRG\b)/i, '');
    for (const trecho of [resto, linhas[i + 1] || '', linhas[i + 2] || '']) {
      const numero = trecho.match(NUMERO_RG);
      if (!numero) continue;
      const digitos = numero[0].replace(/\D/g, '');
      if (digitos.length < 6 || digitos.length > 11 || digitos === cpfDigitos) continue;
      return numero[0];
    }
  }
  return '';
}

// CRLV ou ATPV lido por engano onde se esperava um documento de pessoa (CNH, RG): eles trazem nome e CPF do
// proprietário, que não são os do cliente. A CNH também cita "veículo", por isso ela é excluída antes.
export function pareceDocumentoDeVeiculo(texto) {
  const t = texto || '';
  if (/CARTEIRA\s+NACIONAL\s+DE\s+HABILITA|DRIVER\s+LICENSE/i.test(t)) return false;
  return /REGISTRO\s+E\s+LICENCIAMENTO\s+DE\s+VE[ÍI]CULO|AUTORIZA[ÇC][ÃA]O\s+PARA\s+TRANSFER[ÊE]NCIA\s+DE\s+PROPRIEDADE|C[ÓO]DIGO\s+RENAVAM/i.test(t);
}

// texto: saída bruta do OCR. Devolve só os campos encontrados: { nome, cpfCnpj, rg }
export function extrairDadosDocumento(texto) {
  const original = texto || '';
  if (pareceDocumentoDeVeiculo(original)) return { documentoDeVeiculo: true };
  const linhas = original.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const cpf = extrairCpf(original);
  const dados = { nome: extrairNome(linhas), cpfCnpj: cpf, rg: extrairRg(linhas, cpf) };
  Object.keys(dados).forEach(campo => { if (!dados[campo]) delete dados[campo]; });
  return dados;
}
